import { Layout } from '@/components/layout/Layout';
import { motion } from 'framer-motion';

export default function Terms() {
  return (
    <Layout>
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-4xl font-bold mb-8">Terms of Service</h1>
          <p className="text-muted-foreground mb-8">
            Last updated: {new Date().toLocaleDateString()}
          </p>

          <div className="prose dark:prose-invert max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-semibold mb-4">1. Acceptance of Terms</h2>
              <p className="text-muted-foreground leading-relaxed">
                By accessing or using NexQR ("Service"), you agree to be bound by these
                Terms of Service. If you do not agree to all terms, do not use our Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">2. Description of Service</h2>
              <p className="text-muted-foreground leading-relaxed">
                NexQR provides a platform for creating, managing, and tracking QR codes.
                Features include:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Static and dynamic QR code generation</li>
                <li>QR code customization and branding</li>
                <li>Scan analytics and tracking</li>
                <li>Security features (password protection, expiration, geo-restrictions)</li>
                <li>Team collaboration features</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">3. User Accounts</h2>
              <p className="text-muted-foreground mb-4">
                To access certain features, you must create an account. You agree to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Provide accurate and complete information</li>
                <li>Maintain the security of your password</li>
                <li>Accept responsibility for all activities under your account</li>
                <li>Notify us immediately of any unauthorized use</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">4. Acceptable Use</h2>
              <p className="text-muted-foreground mb-4">
                You agree NOT to use the Service to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Create QR codes linking to malicious, illegal, or harmful content</li>
                <li>Distribute malware, viruses, or malicious code</li>
                <li>Engage in phishing or fraudulent activities</li>
                <li>Infringe on intellectual property rights</li>
                <li>Violate any applicable laws or regulations</li>
                <li>Harass, abuse, or harm others</li>
                <li>Collect or store personal data without consent</li>
                <li>Interfere with or disrupt the Service</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">5. Subscription and Payments</h2>
              <h3 className="text-xl font-medium mb-2">Billing</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Paid subscriptions are billed in advance on a monthly or annual basis.
                You authorize us to charge your payment method for all fees.
              </p>
              
              <h3 className="text-xl font-medium mb-2">Cancellation</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                You may cancel your subscription at any time. Cancellation takes effect
                at the end of the current billing period. No refunds for partial periods.
              </p>

              <h3 className="text-xl font-medium mb-2">Price Changes</h3>
              <p className="text-muted-foreground leading-relaxed">
                We reserve the right to modify pricing. You will be notified 30 days
                before any price changes take effect.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">6. Intellectual Property</h2>
              <p className="text-muted-foreground leading-relaxed">
                The Service and its original content, features, and functionality are
                owned by NexQR and are protected by international copyright, trademark,
                and other intellectual property laws.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">7. User Content</h2>
              <p className="text-muted-foreground leading-relaxed">
                You retain ownership of content you create through our Service. By using
                the Service, you grant us a non-exclusive license to use, store, and
                display your content as necessary to provide the Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">8. Disclaimer of Warranties</h2>
              <p className="text-muted-foreground leading-relaxed">
                THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. WE DO NOT
                WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">9. Limitation of Liability</h2>
              <p className="text-muted-foreground leading-relaxed">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, NEXQR SHALL NOT BE LIABLE FOR
                ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES,
                OR ANY LOSS OF PROFITS OR REVENUES.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">10. Termination</h2>
              <p className="text-muted-foreground leading-relaxed">
                We may terminate or suspend your account immediately, without prior
                notice, for conduct that we determine violates these Terms or is harmful
                to other users, us, or third parties.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">11. Governing Law</h2>
              <p className="text-muted-foreground leading-relaxed">
                These Terms shall be governed by and construed in accordance with the
                laws of the jurisdiction in which NexQR is registered, without regard
                to conflict of law principles.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">12. Changes to Terms</h2>
              <p className="text-muted-foreground leading-relaxed">
                We reserve the right to modify these Terms at any time. We will provide
                notice of significant changes. Continued use of the Service after changes
                constitutes acceptance of the new Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">13. Contact Us</h2>
              <p className="text-muted-foreground leading-relaxed">
                If you have questions about these Terms, please contact us:
              </p>
              <ul className="list-none space-y-2 text-muted-foreground mt-4">
                <li>Email: legal@nexqr.online</li>
              </ul>
            </section>
          </div>
        </motion.div>
      </div>
    </Layout>
  );
}
